import React from 'react'
import styled from '@emotion/styled'
import {HighlightedTitle} from 'components/Home/HighlightedTitle'

import {Button} from '@vio/atlas/Button/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'
import {Team} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {CitiesGroup} from './CitiesGroup'
import {useLocationTopDeals} from './hooks/useLocationTopDeals'
import {HotelsList, HotelsListSkeleton} from './HotelsList'

const TopSection = styled.div`
  display: grid;
  overflow: hidden;
  grid-template-columns: auto min-content;
  grid-template-rows: auto auto;
  grid-template-areas:
    'title button'
    'filter filter';
  grid-row-gap: ${cssTheme.layout.spacing.s400};
  margin-bottom: ${cssTheme.layout.spacing.s500};
  margin-left: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    margin-bottom: ${cssTheme.layout.spacing.s500};
    grid-template-areas:
      'title button'
      'filter button';
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  grid-area: button;
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  grid-area: title;
`
const PillsWrapper = styled.div`
  grid-area: filter;
  width: 0;
  min-width: calc(
    100% + ${cssTheme.layout.spacing.s500} + ${cssTheme.layout.spacing.s500}
  );
  margin-left: ${minus(cssTheme.layout.spacing.s500)};
  margin-right: ${minus(cssTheme.layout.spacing.s500)};
  ${linariaMq.desktopXs} {
    margin-left: 0;
    margin-right: 0;
  }
`

const SeeAllText = styled.span`
  white-space: nowrap;
`

export const TopDealsForTonight = () => {
  const {isMobile} = useDeviceLayout()

  const trackingPayload = {
    component: 'SuggestedProperties',
    team: Team.Retention
  }

  const {
    ref,
    onHotelClick,
    onSeeAllClick,
    handleChangeLocation,
    citiesList,
    selectedCity,
    checkIn,
    checkOut,
    isFetchingGeo,
    isCitiesGroupActive,
    togglePillsActivity
  } = useLocationTopDeals(trackingPayload)

  return (
    <div ref={ref} className="chromatic-ignore">
      <TopSection>
        <TitleWrapper>
          <Text variant="titleXL">
            <HighlightedTitle source="TopDealsForTonight" />
          </Text>
        </TitleWrapper>
        <ButtonWrapper>
          {!isMobile && (
            <Button variant="tertiary" onClick={onSeeAllClick}>
              <SeeAllText>
                <FormattedMessageWrapper
                  id="hotelCard.seeAll"
                  defaultMessage="See all"
                />
              </SeeAllText>
            </Button>
          )}
        </ButtonWrapper>
        <PillsWrapper>
          <CitiesGroup
            items={citiesList}
            onPillClick={handleChangeLocation}
            isActive={!isFetchingGeo && isCitiesGroupActive}
          />
        </PillsWrapper>
      </TopSection>
      <Box display="flex" flexDirection="column" gap="s400">
        {citiesList.length ? (
          citiesList.map(city => (
            <HotelsList
              key={`top-deals-${city.placeId}`}
              placeId={city?.placeId}
              selectedPlaceId={selectedCity?.placeId}
              checkIn={checkIn}
              checkOut={checkOut}
              rooms="2"
              trackingPayload={trackingPayload}
              isFetchingGeo={isFetchingGeo}
              onHotelClick={onHotelClick}
              hasSidePaddings={isMobile}
              numberOfResults={5}
              onRequestStart={togglePillsActivity(false)}
              onRequestComplete={togglePillsActivity(true)}
            />
          ))
        ) : (
          <HotelsListSkeleton />
        )}
      </Box>
      {isMobile && (
        <Box padding="s500" paddingBottom="s000">
          <Button variant="tertiary" fullWidth onClick={onSeeAllClick}>
            <SeeAllText>
              <FormattedMessageWrapper
                id="hotelCard.seeAll"
                defaultMessage="See all"
              />
            </SeeAllText>
          </Button>
        </Box>
      )}
    </div>
  )
}
